import React, { createContext, useContext, useEffect, useState } from "react";
import { useAudioOptions, useCaptionOptions, useMediaPlayer } from "@vidstack/react";
import { getLanguageTracks, getPreferredLanguageTrack, LanguageTrackList, setActiveAudioTrack, setActiveTextTrack } from "@/components/player/language/LanguageTrack";
export interface PlayerLanguageContextInterface {
  selectedLanguage: string;
  setSelectedLanguage: (language: string) => void;
  captionShowing: boolean;
  setCaptionShowing: (showing: boolean) => void;
  languageTracks: LanguageTrackList;
}
export const PlayerLanguageContext = createContext<PlayerLanguageContextInterface>({
  selectedLanguage: '',
  setSelectedLanguage: () => {},
  captionShowing: false,
  setCaptionShowing: () => {},
  languageTracks: []
});
export const PlayerLanguageContextProvider = ({
  children,
  languageOrder,
  captionShowingDefault,
  videoId,
  autoSwitch
}: {
  children: React.ReactNode;
  languageOrder: string[];
  captionShowingDefault?: boolean;
  videoId?: string;
  autoSwitch?: boolean;
}) => {
  const player = useMediaPlayer();
  const audioTracks = player?.audioTracks;
  const textTracks = player?.textTracks;

  // useAudioOptions and useCaptionsOptions are simply used to trigger a re-render when tracks changes outside of this menu.
  const audioOptions = useAudioOptions();
  const captionOptions = useCaptionOptions();
  const [languageTracks, setLanguageTracks] = useState<LanguageTrackList>([]);
  const [selectedLanguage, setSelectedLanguageValue] = useState<string>(() => {
    return sessionStorage.getItem(`selectedLanguage-${videoId}`) || '';
  });
  const [captionShowing, setCaptionShowingValue] = useState<boolean>(false);
  const [captionPrevState, setCaptionPrevState] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    const languagesList = getLanguageTracks(languageOrder || [], audioTracks, textTracks);
    setLanguageTracks(languagesList);
    console.debug('useEffect to set languages. Languages:', languagesList);
  }, [audioOptions, captionOptions, audioTracks, textTracks, languageOrder]);
  const setSelectedLanguage = (targetLanguage: string) => {
    setSelectedLanguageValue(targetLanguage);
    sessionStorage.setItem(`selectedLanguage-${videoId}`, targetLanguage);
    setActiveAudioTrack(languageTracks, targetLanguage);
    setActiveTextTrack(languageTracks, targetLanguage, captionShowing);
  };
  const setCaptionShowing = (showing: boolean) => {
    setCaptionShowingValue(showing);
    sessionStorage.setItem(`captionShowing-${videoId}`, JSON.stringify(showing));
    setActiveTextTrack(languageTracks, selectedLanguage, showing);
  };

  // Use detected language from browser to select audio track and text track language once languages are loaded
  useEffect(() => {
    console.debug('Selected language:', selectedLanguage);
    console.debug('Language tracks:', languageTracks);
    if (languageTracks.length > 0) {
      const storageLanguage = sessionStorage.getItem(`selectedLanguage-${videoId}`);
      if (!autoSwitch && !storageLanguage) {
        console.log("Auto switch disabled");
        const defaultLang = languageTracks.find(track => track.default)?.languageCode;
        setSelectedLanguage(defaultLang || languageTracks[0].languageCode);
      } else {
        const browserLanguage = getPreferredLanguageTrack(languageTracks);
        console.log('Browser language:', browserLanguage);
        if (storageLanguage && languageTracks.find(track => track.languageCode == storageLanguage)) {
          setSelectedLanguage(storageLanguage);
        } else if (browserLanguage && (!selectedLanguage || browserLanguage.languageCode == selectedLanguage)) {
          setSelectedLanguage(browserLanguage.languageCode);
        } else if (!selectedLanguage) {
          setSelectedLanguage(languageTracks[0].languageCode);
        }
      }
    }
  }, [languageTracks]);
  useEffect(() => {
    // Restore caption showing state from sessionStorage
    const storedCaptionState = sessionStorage.getItem(`captionShowing-${videoId}`);
    if (storedCaptionState !== null) {
      // Use the value from sessionStorage if found
      setCaptionShowingValue(JSON.parse(storedCaptionState));
    } else {
      setCaptionShowingValue(!!captionShowingDefault);
    }
  }, [captionShowingDefault, videoId]);
  return <PlayerLanguageContext.Provider value={{
    selectedLanguage,
    setSelectedLanguage,
    captionShowing,
    setCaptionShowing,
    languageTracks
  }} data-sentry-element="unknown" data-sentry-component="PlayerLanguageContextProvider" data-sentry-source-file="PlayerLanguageContext.tsx">
            {children}
        </PlayerLanguageContext.Provider>;
};
export const usePlayerLanguageContext = () => useContext(PlayerLanguageContext);